import React, { memo, useCallback, useMemo, useState } from 'react';

import GroupSelectMenuSearch from '@components/common/GroupSelectKpi/GroupSelectMenu/GroupSelectMenuSearch';
import FlatTreeMavenir from '@components/routes/CmModule/ConfigurationComparison/MOTreeMavenir/FlatTreeMavenir';

import { IFlatMoTreeNode } from '@interfaces/components/tree';

import {
	clearSearchText,
	filterNodesBySearchText,
	getCountOfCheckedParameters,
	sortNodesAlphabetically,
	sortNodesByCheckedCount,
} from './utils';

import styles from './TreeGroupSelect.module.scss';

interface ITreeGroupSelect {
	data: IFlatMoTreeNode[];
	isDisabled?: boolean;
	isDisabledSubmit?: boolean;
	isIgnoreTreeChecked?: boolean;
	maxSelectionNumber?: number | null;
	maxSelectionText?: string;
	onSubmit: () => void;
	resetHandler: () => void;
	searchPlaceholder: string;
	setData: React.Dispatch<React.SetStateAction<IFlatMoTreeNode[]>> | undefined;
}

const TreeGroupSelect = ({
	data,
	isDisabled = false,
	isDisabledSubmit,
	isIgnoreTreeChecked,
	maxSelectionNumber,
	maxSelectionText,
	onSubmit,
	resetHandler,
	searchPlaceholder,
	setData,
}: ITreeGroupSelect) => {
	const [isCheckedSort, setIsCheckedSort] = useState<boolean>(true);
	const [searchValue, setSearchValue] = useState('');

	const isMaxSelectionError = useMemo(() => {
		return maxSelectionNumber ? getCountOfCheckedParameters(data) > maxSelectionNumber : false;
	}, [data, maxSelectionNumber]);

	const setCheckedSort = useCallback(() => setData?.((prev) => sortNodesByCheckedCount(prev)), [setData]);
	const setAlphabetSort = useCallback(() => setData?.((prev) => sortNodesAlphabetically(prev)), [setData]);

	const onChangeSort = useCallback(() => {
		setIsCheckedSort(prevState => {
			if (prevState) {
				setAlphabetSort();
			} else {
				setCheckedSort();
			}

			return !prevState;
		});
	}, [setAlphabetSort, setCheckedSort]);

	const onSearchHandler = useCallback((searchText: string) => {
		setSearchValue(searchText);
		setData?.((prev) => filterNodesBySearchText(prev)(searchText));
	}, [setData]);

	const onSearchClear = useCallback(() => {
		setSearchValue('');
		setData?.(prev => clearSearchText(prev));
	}, [setData]);

	const resetAndClearSearch = useCallback(() => {
		resetHandler();
		setSearchValue('');
	}, [resetHandler]);

	return (
		<div className={styles.wrapper}>
			<GroupSelectMenuSearch
				isCheckedSort={isCheckedSort}
				isMaxSelectionError={isMaxSelectionError}
				maxSelectionText={maxSelectionText}
				onChangeSort={onChangeSort}
				onSearchClear={onSearchClear}
				onSearchHandler={onSearchHandler}
				placeholder={searchPlaceholder}
				readOnly={false}
				refreshOptions={setCheckedSort}
				searchThreshold={0}
				searchValue={searchValue}
			/>
			<FlatTreeMavenir
				cancelHandler={resetHandler}
				cancelText='Reset changes'
				customCancelHandler={resetAndClearSearch}
				data={data}
				isDisabled={isDisabled}
				isDisabledSubmit={isMaxSelectionError || isDisabledSubmit}
				isIgnoreTreeChecked={isIgnoreTreeChecked}
				isMaxSelectionError={isMaxSelectionError}
				isWithFolderIcon={false}
				setData={setData}
				submitHandler={onSubmit}
				submitText='Save'
			/>
		</div>
	);
};

export default memo(TreeGroupSelect);
